import React, { useState, useRef, useEffect, useMemo } from 'react';

interface EditableLabelProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  label: string;
  unit: string;
  showPlayPause?: boolean;
  isRotating?: boolean;
  onRotationToggle?: () => void;
  hasImage?: boolean;
}

const EditableLabel: React.FC<EditableLabelProps> = ({ 
  value, 
  onChange, 
  min, 
  max, 
  label, 
  unit,
  showPlayPause,
  isRotating,
  onRotationToggle,
  hasImage = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  const [hasChanged, setHasChanged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);

  const formattedValue = useMemo(() => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    let formattedNum;
    
    if (label.toUpperCase() === 'PETALS') {
      formattedNum = Math.round(absValue).toString();
    } else if (label === 'Seam') {
      formattedNum = (isHovering || isEditing || absValue !== 2) ? 
        absValue.toFixed(1) : 
        absValue.toString();
    } else {
      formattedNum = (isHovering || isEditing) ? absValue.toFixed(1) : Math.round(absValue).toString();
    }
    
    return {
      numValue: `${isNegative ? '-' : ''}${formattedNum}`,
      fullValue: `${isNegative ? '-' : ''}${formattedNum}${unit}`
    };
  }, [value, label, unit, isHovering, isEditing]);
  
  useEffect(() => {
    if (isEditing && inputRef.current) {
      if (valueRef.current) {
        inputRef.current.style.width = `${valueRef.current.offsetWidth}px`;
      }
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(formattedValue.fullValue);
    }
  }, [value, isEditing, formattedValue.fullValue]);

  useEffect(() => {
    if (value !== 2) {
      setHasChanged(true);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsTyping(true);
  };

  const handleBlur = () => {
    let finalValue = inputValue.replace(unit, '');
    
    if (finalValue === '' || finalValue === '-' || finalValue === '.' || finalValue === '-.') {
      finalValue = '0';
    }
    
    const parsed = parseFloat(finalValue);
    const clampedValue = Math.min(Math.max(parsed, min), max);
    
    onChange(clampedValue);
    setIsEditing(false);
    setIsHovering(false);
    setIsTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setInputValue(formattedValue.fullValue);
      setIsEditing(false);
      setIsHovering(false);
      setIsTyping(false);
    }
  };

  const handleClick = () => {
    setIsEditing(true);
    setInputValue(formattedValue.fullValue);
    setIsTyping(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };

  const handleMouseEnter = () => {
    if (!isEditing) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isEditing) {
      setIsHovering(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    alignItems: 'center',
    height: '24px',
    margin: '0',
    color: '#999999',
    gap: '8px'
  };

  const valueContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '19px',
    lineHeight: '1',
    userSelect: 'none',
    letterSpacing: '0.05em',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
    position: 'relative',
    width: '100%'
  };

  const valueGroupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    position: 'absolute',
    left: '51px',
    right: '24px',  // Add fixed space for the button
    letterSpacing: '0.05em',
    justifyContent: 'space-between'
};

  const labelStyle: React.CSSProperties = {
    color: '#999999',
    fontSize: '14px',
    letterSpacing: '0.05em',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
    textTransform: 'uppercase',
    paddingLeft: '16px',
    display: 'flex',
    alignItems: 'center'
  };

  const inputStyle: React.CSSProperties = {
    border: 'none',
    padding: '0 4px',
    fontSize: '19px',
    fontWeight: '600',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    letterSpacing: '0.05em',
    outline: 'none',
    background: isTyping ? '#333333' : 'transparent',
    borderRadius: '2px',
    caretColor: '#00a000',
    color: isTyping ? '#CCCCCC' : '#999999',
    width: 'auto',
    minWidth: '60px',
    boxSizing: 'border-box',
    WebkitTextFillColor: isTyping ? '#CCCCCC' : '#999999'
  };

  return (
    <>
      <style>{`
        .editable-label input::selection {
          background-color: rgba(0, 160, 0, 0.4) !important;
          color: #CCCCCC !important;
        }
      `}</style>
      <div style={containerStyle} className="editable-label">
        <div style={labelStyle}>
          <span style={{ color: '#999999' }}>
            {label.toUpperCase()}
          </span>
        </div>
        <div
          style={valueContainerStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div style={valueGroupStyle}>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => e.target.select()}
                  style={inputStyle}
                />
              ) : (
                <div 
                  ref={valueRef}
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                >
                  {unit === 'px' ? (
                    <>
                      {formattedValue.numValue}
                      <span style={{ fontSize: '14px' }}>PX</span>
                    </>
                  ) : (
                    <>
                      {formattedValue.numValue}{unit}
                    </>
                  )}
                </div>
              )}
            </div>
            {showPlayPause && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onRotationToggle?.();
    }}
    style={{ 
      background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
    position: 'absolute',  // Add this
    right: '-24px',       // Add this to align with container
    top: '50%',           // Add this
    transform: 'translateY(-50%)'  // Add this to vertically center
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(0, 160, 0, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
  >
    {isRotating ? (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M8 5v14M16 5v14" stroke="#00a000" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: hasImage ? 1 : 0.5 }}/>
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="0">
        <path d="M6 3.5c0-.828.667-1.5 1.5-1.5.321 0 .63.103.886.294l11.5 8.5c.489.36.489 1.096 0 1.456l-11.5 8.5A1.5 1.5 0 016 19.5v-16z" fill="#00a000"/>
      </svg>
    )}
  </button>
)}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditableLabel;